import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchContactPosts } from '../../../redux/FeedSlice/feedSlice';
import { checkLinkedInConnectionStatus, fetchAllConnections } from '../../../redux/LIAuthSlice/liAuthSlice';

import Sidebar from '../../../components/Navbar/SideNavbar';
import ConnectLinkedInHero from '../../../components/common/ConnectLinkedInHero';
import Navbaar from '../../../components/Navbar/Navbaar';
import PostsCard from '../../../components/MyContactPage/PostsCard';
import { isUsablePhoto } from '../../../components/NetworkingPage/NetContent/NetPipelineRow';

const AVATAR_GRADIENTS = [
    '#3F57E0', '#7C3AED', '#059669', '#DC2626', '#B45309', '#0369A1', '#9333EA', '#1B6E47',
];
const initials = (name) => {
    if (!name) return '?';
    const parts = String(name).trim().split(' ').filter(Boolean);
    return (parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : parts[0].slice(0, 2)).toUpperCase();
};
const gradientFor = (name) => {
    if (!name) return AVATAR_GRADIENTS[0];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
    return AVATAR_GRADIENTS[Math.abs(h) % AVATAR_GRADIENTS.length];
};

// Gradient-initials avatar with photo fallback (matches the design's Avatar).
function ConnAvatar({ name, photo, size = 34 }) {
    if (isUsablePhoto(photo)) {
        return (
            <img
                src={photo}
                alt={name || ''}
                className="rounded-full object-cover flex-shrink-0"
                style={{ width: size, height: size }}
                onError={(e) => { e.target.style.display = 'none'; }}
            />
        );
    }
    return (
        <div
            className="rounded-full grid place-items-center text-white font-bold flex-shrink-0 select-none"
            style={{
                width: size, height: size, fontSize: size * 0.36,
                background: `linear-gradient(135deg, ${gradientFor(name)}, #6377F0)`,
                boxShadow: 'inset 0 -1px 2px rgba(0,0,0,.15)',
            }}
        >
            {initials(name)}
        </div>
    );
}

// ─── Skeleton loaders ───────────────────────────────────────────────
function PostSkeleton() {
    return (
        <div className="bg-white rounded-xl border border-[#E6E8EE] mb-4 p-5 animate-pulse" style={{ boxShadow: '0 1px 2px rgba(15,23,42,0.04)' }}>
            <div className="flex items-center gap-3">
                <div className="w-[46px] h-[46px] rounded-full bg-[#EAECF2]" />
                <div className="flex-1 space-y-2">
                    <div className="h-3 w-40 bg-[#EAECF2] rounded" />
                    <div className="h-2.5 w-56 bg-[#F0F1F5] rounded" />
                    <div className="h-2.5 w-28 bg-[#F0F1F5] rounded" />
                </div>
            </div>
            <div className="mt-4 space-y-2">
                <div className="h-2.5 w-full bg-[#F0F1F5] rounded" />
                <div className="h-2.5 w-[92%] bg-[#F0F1F5] rounded" />
                <div className="h-2.5 w-[68%] bg-[#F0F1F5] rounded" />
            </div>
            <div className="mt-4 flex items-center gap-2">
                <div className="h-4 w-16 bg-[#F0F1F5] rounded-full" />
                <div className="flex-1" />
                <div className="h-3 w-20 bg-[#F0F1F5] rounded" />
            </div>
            <div className="mt-4 border-t border-[#EFF1F5] pt-3 grid grid-cols-3 gap-3">
                {[0, 1, 2].map(i => <div key={i} className="h-4 bg-[#F0F1F5] rounded mx-6" />)}
            </div>
        </div>
    );
}

function ConnectionRowSkeleton() {
    return (
        <div className="flex items-center gap-2.5 p-2 animate-pulse">
            <div className="w-[34px] h-[34px] rounded-full bg-[#EAECF2]" />
            <div className="flex-1 space-y-1.5">
                <div className="h-2.5 w-28 bg-[#EAECF2] rounded" />
                <div className="h-2 w-40 bg-[#F0F1F5] rounded" />
            </div>
        </div>
    );
}

const Feed = () => {
    const dispatch = useDispatch();

    const user = useSelector(state => state.auth.user);
    const { isLinkedInConnected, connections, connectionsLoading, connectionChecked } = useSelector((state) => state.liauth);
    const { postsByProviderId } = useSelector((state) => state.feeds);

    const [selectedConnection, setSelectedConnection] = useState(null);
    const [connectionSearch, setConnectionSearch] = useState('');
    const [sortBy, setSortBy] = useState('recent'); // 'top' | 'recent'
    const [showAllConnections, setShowAllConnections] = useState(false);

    const CONNECTIONS_PREVIEW = 6; // how many to show before "View all"

    const {
        data: posts = [],
        loading: postsLoading,
        error: postsError
    } = postsByProviderId[selectedConnection?.providerId] || {};

    useEffect(() => {
        if (user?._id) {
            dispatch(checkLinkedInConnectionStatus());
        }
    }, [dispatch, user?._id]);

    useEffect(() => {
        if (isLinkedInConnected?.accountId && connections.length === 0) {
            dispatch(fetchAllConnections({ accountId: isLinkedInConnected.accountId }));
        }
    }, [dispatch, isLinkedInConnected?.accountId]);

    useEffect(() => {
        if (selectedConnection?.providerId && isLinkedInConnected?.accountId) {
            dispatch(fetchContactPosts({
                providerId: selectedConnection.providerId,
                accountId: isLinkedInConnected.accountId,
                limit: 10
            }));
        }
    }, [dispatch, selectedConnection, isLinkedInConnected?.accountId]);

    const filteredConnections = useMemo(() => {
        const q = connectionSearch.trim().toLowerCase();
        if (!q) return connections;
        return connections.filter(c =>
            (c.name || '').toLowerCase().includes(q) ||
            (c.title || '').toLowerCase().includes(q) ||
            (c.organization_name || '').toLowerCase().includes(q)
        );
    }, [connections, connectionSearch]);

    // While searching, always show every match; otherwise show a short preview
    // until the user taps "View all".
    const isSearchingConnections = connectionSearch.trim().length > 0;
    const displayedConnections = (showAllConnections || isSearchingConnections)
        ? filteredConnections
        : filteredConnections.slice(0, CONNECTIONS_PREVIEW);

    const sortedPosts = useMemo(() => {
        const arr = [...posts];
        if (sortBy === 'top') {
            arr.sort((a, b) => (b.reactionCounter || 0) - (a.reactionCounter || 0));
        } else {
            arr.sort((a, b) => new Date(b.parsedDatetime || 0) - new Date(a.parsedDatetime || 0));
        }
        return arr;
    }, [posts, sortBy]);

    const connRole = (c) => [c.title, c.organization_name].filter(Boolean).join(' · ');

    return (
        <div className="relative bg-[#F6F7FB] h-screen pt-16 flex flex-col">
            <Navbaar />
            <div className="flex flex-1 min-h-0 overflow-hidden">
                <div className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0">
                    <Sidebar />
                </div>

                <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                    {connectionChecked && !isLinkedInConnected?.connected ? (
                        <ConnectLinkedInHero />
                    ) : (
                    <div className="flex-1 overflow-y-auto p-6">
                        <div
                            className="grid items-start"
                            style={{ gridTemplateColumns: '1fr 320px', gap: 18 }}
                        >
                            {/* CENTER — feed */}
                            <div>
                                {/* Sort header */}
                                <div className="flex items-center gap-2 mb-3.5">
                                    <span className="text-[15px] font-extrabold text-[#0F172A]" style={{ letterSpacing: '-0.01em' }}>Latest updates</span>
                                    <span className="flex-1" />
                                    <span className="text-[11.5px] font-semibold text-[#64748B]">Sort by</span>
                                    <select
                                        value={sortBy}
                                        onChange={e => setSortBy(e.target.value)}
                                        className="border border-[#E6E8EE] rounded-lg px-2.5 py-[5px] text-[12px] font-semibold bg-white text-[#334155] cursor-pointer focus:outline-none focus:border-[#3F57E0]"
                                    >
                                        <option value="top">Top</option>
                                        <option value="recent">Recent</option>
                                    </select>
                                </div>

                                {/* Posts */}
                                {!selectedConnection ? (
                                    <div className="flex flex-col items-center justify-center text-center bg-white rounded-xl border border-[#E6E8EE] py-20" style={{ boxShadow: '0 1px 2px rgba(15,23,42,0.04)' }}>
                                        <div className="w-12 h-12 rounded-2xl bg-[#EEF1FE] flex items-center justify-center mb-3">
                                            <svg className="w-6 h-6 text-[#3F57E0]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" /></svg>
                                        </div>
                                        <p className="text-[13.5px] font-bold text-[#0F172A]">Select a connection</p>
                                        <p className="text-[11.5px] text-[#64748B] mt-1">Choose someone from your connections to see their latest posts.</p>
                                    </div>
                                ) : postsLoading ? (
                                    <>
                                        {[0, 1, 2].map(i => <PostSkeleton key={i} />)}
                                    </>
                                ) : postsError ? (
                                    <div className="flex items-center justify-center py-16 bg-white rounded-xl border border-[#E6E8EE]">
                                        <p className="text-[12.5px] text-red-500">Error loading posts. Please try again.</p>
                                    </div>
                                ) : sortedPosts.length === 0 ? (
                                    <div className="flex items-center justify-center py-16 bg-white rounded-xl border border-[#E6E8EE]">
                                        <p className="text-[12.5px] text-[#64748B]">No posts found for {selectedConnection.name}.</p>
                                    </div>
                                ) : (
                                    sortedPosts.map(post => (
                                        <PostsCard
                                            key={post._id || post.socialId}
                                            selectedConnection={selectedConnection}
                                            post={post}
                                            accountId={isLinkedInConnected?.accountId}
                                        />
                                    ))
                                )}
                            </div>

                            {/* RIGHT — connections card (sticky) */}
                            <div className="sticky" style={{ top: 0 }}>
                                <div className="bg-white border border-[#E6E8EE] rounded-xl px-5 py-[18px]" style={{ boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 1px 3px rgba(15,23,42,0.06)' }}>
                                    {/* Header */}
                                    <div className="flex items-center gap-2 mb-2.5">
                                        <span className="text-[10.5px] font-semibold uppercase text-[#94A3B8]" style={{ letterSpacing: '0.08em' }}>Your connections</span>
                                        <span className="flex-1" />
                                        <span className="text-[11.5px] font-semibold text-[#64748B]">{connections.length}</span>
                                    </div>

                                    {/* Search */}
                                    <div className="flex items-center gap-2 bg-[#F1F3F7] rounded-[10px] px-2.5 py-[7px] mb-2.5 border border-transparent focus-within:bg-white focus-within:border-[#E0E5FC]">
                                        <svg className="w-[13px] h-[13px] text-[#94A3B8] shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path strokeLinecap="round" d="M21 21l-4.3-4.3" /></svg>
                                        <input
                                            value={connectionSearch}
                                            onChange={e => setConnectionSearch(e.target.value)}
                                            placeholder="Search connections…"
                                            className="flex-1 bg-transparent outline-none text-[13px] text-[#334155] placeholder-[#94A3B8]"
                                        />
                                    </div>

                                    {/* List */}
                                    <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 260px)' }}>
                                        {connectionsLoading ? (
                                            <div className="space-y-1">
                                                {[...Array(6)].map((_, i) => <ConnectionRowSkeleton key={i} />)}
                                            </div>
                                        ) : filteredConnections.length === 0 ? (
                                            <div className="py-8 text-center">
                                                <p className="text-[12px] text-[#64748B]">{connectionSearch ? 'No matches.' : 'No connections yet.'}</p>
                                            </div>
                                        ) : (
                                            displayedConnections.map((c) => {
                                                const isActive = selectedConnection?.providerId === c.providerId;
                                                return (
                                                    <div
                                                        key={c.providerId}
                                                        onClick={() => setSelectedConnection(c)}
                                                        className="flex items-center gap-2.5 p-2 rounded-[10px] cursor-pointer transition-colors hover:bg-[#F6F7FB]"
                                                        style={{
                                                            background: isActive ? '#EEF1FE' : 'transparent',
                                                            borderLeft: isActive ? '3px solid #3F57E0' : '3px solid transparent',
                                                        }}
                                                    >
                                                        <ConnAvatar name={c.name} photo={c.profile_picture} size={34} />
                                                        <div className="flex-1 min-w-0 text-left">
                                                            <div className="text-[12.5px] font-bold text-[#0F172A] truncate text-left">{c.name}</div>
                                                            <div className="text-[11.5px] text-[#64748B] truncate text-left">{connRole(c)}</div>
                                                        </div>
                                                        {c.linkedin_url && (
                                                            <a
                                                                href={c.linkedin_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="w-7 h-7 grid place-items-center rounded-lg text-[#0A66C2] hover:bg-white transition-colors shrink-0"
                                                                title="View LinkedIn profile"
                                                            >
                                                                <svg className="w-[14px] h-[14px]" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" /><circle cx="4" cy="4" r="2" /></svg>
                                                            </a>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>

                                    {!isSearchingConnections && filteredConnections.length > CONNECTIONS_PREVIEW && (
                                        <button
                                            onClick={() => setShowAllConnections(v => !v)}
                                            className="w-full mt-2 py-2 rounded-lg text-[12px] font-semibold text-[#334155] hover:bg-[#F1F3F7] transition-colors"
                                        >
                                            {showAllConnections ? 'Show less' : `View all ${filteredConnections.length} →`}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Feed;
